"use client";

import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";
import { ItemCard } from "@/components/search/item-card";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemCardSkeletonRow } from "@/components/search/item-card-skeleton";

interface Listing {
  id: string;
  title: string;
  price: string;
  currency?: string | null;
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: kept as part of the fixture
  // shape for `ItemCard` (which does not render any location chip). The
  // suggestions API now emits `shipFromAddress`; this field is not used
  // by ItemCard so we drop the read on input.
  image_urls: string | string[];
  description: string;
  condition: string;
  grade: string;
  created_at: string;
  username?: string;
  avatar_url?: string | null;
  quantity?: number;
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  type?: string | null;
  user_id?: string;
  // Server-computed saved (bookmark) count from /api/listings/suggestions.
  // Absent when the RPC soft-failed → ItemCard degrades to "no count".
  saves_count?: number;
  card_details?: {
    tcg_name?: string | null;
    set_name?: string | null;
    rarity_name?: string | null;
    condition_name?: string | null;
    grading_company_name?: string | null;
    grade_value?: number | null;
    grade_label?: string | null;
    badges?: string[];
    defects?: string[];
  } | null;
}

interface VerticalListingSuggestionsProps {
  currentListingId: string;
  sellerId: string;
  sellerUsername: string;
  currentUserId?: string;
  currentListingTitle?: string;
  currentListingDescription?: string;
  onLoadingChange?: (loading: boolean) => void;
  enabled?: boolean;
}

function normalizeForSimilarity(value: string): string {
  return value.toLowerCase().trim().slice(0, 160);
}

function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1,
        );
      }
    }
  }
  
  return matrix[b.length][a.length];
}

function calculateSimilarity(str1: string, str2: string): number {
  const normalized1 = normalizeForSimilarity(str1);
  const normalized2 = normalizeForSimilarity(str2);
  const maxLen = Math.max(normalized1.length, normalized2.length);
  if (maxLen === 0) return 1;
  const distance = levenshteinDistance(normalized1, normalized2);
  return 1 - distance / maxLen;
}

export function VerticalListingSuggestions({
  currentListingId,
  sellerId,
  sellerUsername,
  currentUserId,
  currentListingTitle = "",
  currentListingDescription = "",
  onLoadingChange,
  enabled = true,
}: VerticalListingSuggestionsProps) {
  const t = useTranslations("listing");
  const [suggestedItems, setSuggestedItems] = useState<Listing[]>([]);
  const [sellerItems, setSellerItems] = useState<Listing[]>([]);
  const [relatedItems, setRelatedItems] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const isSm = useMediaQuery("(min-width: 640px)");
  const isLg = useMediaQuery("(min-width: 1024px)");
  const carouselCount = isLg ? 10 : isSm ? 8 : 6;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function fetchSuggestions() {
      setLoading(true);
      try {
        const suggestedLimit = Math.max(24, carouselCount * 6);
        const sellerLimit = Math.max(12, carouselCount * 3);
        const params = new URLSearchParams({
          currentListingId,
          sellerId,
          suggestedLimit: String(suggestedLimit),
          sellerLimit: String(sellerLimit),
        });
        const res = await fetch(`/api/listings/suggestions?${params.toString()}`);
        if (!res.ok) {
          throw new Error(`Failed to fetch suggestions: ${res.status}`);
        }
        const json = (await res.json().catch(() => null)) as any;
        const suggestedData = Array.isArray(json?.suggested) ? json.suggested : [];
        const sellerData = Array.isArray(json?.seller) ? json.seller : [];
        const filteredSuggested = suggestedData;
        
        const formatItem = (item: any): Listing => ({
          id: item.id,
          title: item.title,
          price: item.price?.toString() ?? "0",
          currency: item.currency ?? null,
          image_urls: item.image_urls,
          description: item.description ?? "",
          condition: item.condition ?? "",
          grade: item.grade ?? "",
          created_at: item.created_at ?? "",
          username: item.username ?? "Unknown User",
          avatar_url: item.avatar_url ?? null,
          quantity: item.quantity,
          scheduled_publish_at: item.scheduled_publish_at,
          listing_format: item.listing_format ?? null,
          type: (item as { type?: string | null }).type ?? null,
          user_id: item.user_id,
          saves_count: typeof item.saves_count === "number" ? item.saves_count : undefined,
          card_details: item.card_details ?? null,
        });

        const recommendedItems = filteredSuggested
          .map(formatItem)
          .sort(() => Math.random() - 0.5)
          .slice(0, carouselCount);

        const relatedItems = filteredSuggested
          .map((item: any) => {
            const titleSim = calculateSimilarity(currentListingTitle, item.title || "");
            const descSim = calculateSimilarity(currentListingDescription, item.description || "");
            const combinedSim = (titleSim * 0.7 + descSim * 0.3);
            return { ...formatItem(item), similarity: combinedSim };
          })
          .filter((item: any) => item.similarity > 0.1)
          .sort((a: any, b: any) => b.similarity - a.similarity)
          .slice(0, carouselCount)
          .map(({ similarity, ...item }: any) => item);

        const formattedSeller = sellerData
          .map(formatItem)
          .sort(() => Math.random() - 0.5)
          .slice(0, carouselCount);

        if (!cancelled) {
          setSuggestedItems(recommendedItems);
          setSellerItems(formattedSeller);
          setRelatedItems(relatedItems);
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    timer = setTimeout(() => {
      if (!cancelled) {
        fetchSuggestions();
      }
    }, 0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, currentListingId, sellerId, currentUserId, carouselCount, currentListingTitle, currentListingDescription]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  if (!enabled) {
    return null;
  }

  if (loading) {
    return (
      <>
        <div className="mt-4">
          <div className="-mx-4 mb-4 px-4">
            <Skeleton className="h-7 w-52 rounded-full bg-white/10 animate-none" />
          </div>
          <ItemCardSkeletonRow
            count={carouselCount}
            keyPrefix="vertical-suggestions-seller"
            tone="inverse"
            containerClassName="-mx-4 px-4 md:mx-0 md:px-0"
            rowClassName="gap-3 overflow-x-auto hide-scrollbar pb-2"
            itemClassName="w-40 sm:w-40 md:w-40 lg:w-40"
          />
        </div>
        <div className="mt-4">
          <div className="-mx-4 mb-4 px-4">
            <Skeleton className="h-7 w-44 rounded-full bg-white/10 animate-none" />
          </div>
          <ItemCardSkeletonRow
            count={carouselCount}
            keyPrefix="vertical-suggestions-related"
            tone="inverse"
            containerClassName="-mx-4 px-4 md:mx-0 md:px-0"
            rowClassName="gap-3 overflow-x-auto hide-scrollbar pb-2"
            itemClassName="w-40 sm:w-40 md:w-40 lg:w-40"
          />
        </div>
      </>
    );
  }

  return (
    <>
      {sellerItems.length > 0 && (
        <CarouselSection title={`More from ${sellerUsername}`} items={sellerItems} currentUserId={currentUserId} />
      )}
      {suggestedItems.length > 0 && (
        <CarouselSection title={t("youMightLike")} items={suggestedItems} currentUserId={currentUserId} />
      )}
      {relatedItems.length > 0 && (
        <CarouselSection title={t("relatedItems")} items={relatedItems} currentUserId={currentUserId} />
      )}
    </>
  );
}

function CarouselSection({
  title,
  items,
  currentUserId,
}: {
  title: string;
  items: Listing[];
  currentUserId?: string;
}) {
  return (
    <div className="mt-4">
      <h3 className="text-xl font-bold text-foreground mb-4 -mx-4 px-4">{title}</h3>
      <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-2 -mx-4 px-4">
        {items.map((item) => (
          <div key={item.id} className="shrink-0 w-40 min-w-0">
            <ItemCard
              listingId={item.id}
              title={item.title}
              price={item.price}
              currency={item.currency ?? null}
              image_urls={item.image_urls}
              stock={item.quantity}
              scheduledPublishAt={item.scheduled_publish_at}
              listingFormat={item.listing_format ?? null}
              listingType={item.type ?? null}
              currentUserId={currentUserId}
              sellerId={item.user_id}
              sellerName={item.username}
              sellerAvatarUrl={item.avatar_url}
              condition={item.condition}
              cardDetails={item.card_details ?? null}
              description={item.description}
              savesCount={item.saves_count}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

